/**
 * Filter Count Component
 *
 * Displays the count of active filters in the format "(N)" or empty string if zero.
 *
 * @component filter-count
 * @extends HTMLElement
 *
 * @description
 * Counts checked filter inputs and displays the count. Can be configured to count:
 * - All filters (param="*")
 * - Specific filter type (param="filter.v.option.Size")
 *
 * The component recursively counts through parent-child hierarchies, so if a parent
 * has 3 checked children, it counts as 4 total (parent + 3 children).
 *
 * @behavior
 * - Automatically ignores `.hidden` inputs
 * - Always ignores `filter.v.availability` (internal filter)
 * - Updates on 'filter:change' and 'filter:count' events
 * - Displays "(N)" when count > 0, empty string when count = 0
 *
 * @attributes
 * @attr {string} param - Filter parameter to count, or "*" for all filters
 *
 * @listens filter:change - Re-counts active filters (from collection-filter)
 * @listens filter:count - Re-counts active filters (triggered manually for sync)
 *
 * @example
 * ```liquid
 * <!-- Count all active filters -->
 * <filter-count param="*"></filter-count>
 * <!-- Output: "(3)" if 3 filters are active, "" if none -->
 *
 * <!-- Count only size filters -->
 * <filter-count param="{{ size_param }}"></filter-count>
 * <!-- Output: "(2)" if 2 sizes are selected -->
 * ```
 *
 * @example
 * Typical usage in filter bar:
 * ```liquid
 * <button>
 *   Size <filter-count param="{{ size_param }}"></filter-count>
 * </button>
 * <!-- Renders as: "Size (2)" when 2 sizes are selected -->
 * ```
 *
 * @dependencies
 * - collection-filter.js - Provides filter Map and dispatches events
 */
class FilterCount extends HTMLElement {
  #collectionFilter = null;

  connectedCallback() {
    // Find parent collection-filter
    this.#collectionFilter = this.closest('collection-filter');

    if (!this.#collectionFilter) {
      console.warn('filter-count: No collection-filter parent found');
      return;
    }

    // Get param attribute (e.g., "*" or "filter.v.option.Size")
    this.param = this.getAttribute('param') || '*';

    // Initial count
    this.updateCount();

    // Listen for filter changes and count updates
    this.#collectionFilter.addEventListener('filter:change', this.handleFilterChange);
    this.#collectionFilter.addEventListener('filter:count', this.handleFilterChange);
  }

  handleFilterChange = () => {
    this.updateCount();
  };

  updateCount() {
    const count = this.countActiveFilters();

    // Clear content if zero, otherwise show count
    if (count === 0) {
      this.textContent = '';
    } else {
      this.textContent = `(${count})`;
    }
  }

  /**
   * Recursively counts active (checked) filters.
   *
   * @returns {number} Total count of checked filters
   *
   * @description
   * Recursively traverses the filter hierarchy to count all checked inputs.
   * Respects parent-child relationships from collection-filter's filter Map.
   *
   * Counting logic:
   * - If parent is checked → count += 1
   * - If parent has children → recursively count each child
   * - Ignores hidden inputs and availability filter
   *
   * @example
   * // Filter hierarchy:
   * // ☑ Clothing (parent)
   * //   ☑ Tops (child)
   * //   ☑ Bottoms (child)
   * //
   * // Returns: 3 (parent + 2 children)
   */
  countActiveFilters() {
    const filters = this.#collectionFilter.filters;

    if (!filters) {
      return 0;
    }

    /**
     * Recursively counts a filter item and its children.
     * @param {FilterItem} item - Filter item from the Map
     * @returns {number} Count of checked inputs in this branch
     */
    const countWithChildren = (item) => {
      let itemCount = item.input.checked ? 1 : 0;

      if (item.children?.length > 0) {
        for (const childId of item.children) {
          const child = filters.get(childId);

          if (child.input.classList.contains('hidden')) {
            continue;
          }

          itemCount += countWithChildren(child);
        }
      }

      return itemCount;
    };

    let count = 0;

    for (const item of filters.values()) {
      if (item.input.classList.contains('hidden')) {
        continue;
      } else if (this.param === '*' && item.param === 'filter.v.availability') {
        continue;
      } else if (this.param !== item.param && this.param !== '*') {
        continue;
      }

      count += countWithChildren(item);
    }

    return count;
  }

  disconnectedCallback() {
    this.#collectionFilter.removeEventListener('filter:change', this.handleFilterChange);
    this.#collectionFilter.removeEventListener('filter:count', this.handleFilterChange);
  }
}

if (!customElements.get('filter-count')) {
  customElements.define('filter-count', FilterCount);
}
